import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { ChevronLeft, ChevronRight, Plus, Pencil, Trash2, Calendar as CalIcon } from 'lucide-react';
import { toast } from 'sonner';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const COLOR_MAP = {
  blue: 'bg-[#4a9ac5]/15 text-[#2a6496] border-l-[#4a9ac5]',
  green: 'bg-emerald-50 text-emerald-700 border-l-emerald-500',
  emerald: 'bg-emerald-50 text-emerald-700 border-l-emerald-500',
  orange: 'bg-orange-50 text-orange-700 border-l-orange-500',
  amber: 'bg-amber-50 text-amber-700 border-l-amber-500',
  red: 'bg-red-50 text-red-700 border-l-red-500',
  violet: 'bg-violet-50 text-violet-700 border-l-violet-500',
  gray: 'bg-gray-50 text-gray-500 border-l-gray-300',
};
const COLORS = ['blue', 'green', 'orange', 'red', 'violet', 'amber'];

export default function CalendarPage() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editEvent, setEditEvent] = useState(null);
  const [form, setForm] = useState({ title: '', date: '', time: '', color: 'blue', description: '', assignee: '' });

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  useEffect(() => {
    api.get('/calendar/events').then(r => setEvents(r.data)).catch(() => {});
  }, []);

  const calendarDays = useMemo(() => {
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const daysInPrev = new Date(year, month, 0).getDate();
    const days = [];
    for (let i = firstDay - 1; i >= 0; i--) days.push({ day: daysInPrev - i, current: false });
    for (let i = 1; i <= daysInMonth; i++) days.push({ day: i, current: true });
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) days.push({ day: i, current: false });
    return days;
  }, [year, month]);

  const getDateStr = (day) => {
    const m = String(month + 1).padStart(2, '0');
    const d = String(day).padStart(2, '0');
    return `${year}-${m}-${d}`;
  };

  const eventsForDate = (dateStr) => events.filter(e => e.date === dateStr);
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const goToday = () => setCurrentDate(new Date());

  const handleSave = async () => {
    try {
      if (editEvent) { await api.put(`/calendar/events/${editEvent.id}`, form); toast.success('Event updated'); }
      else { await api.post('/calendar/events', form); toast.success('Event created'); }
      setDialogOpen(false); setEditEvent(null);
      const r = await api.get('/calendar/events'); setEvents(r.data);
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    await api.delete(`/calendar/events/${id}`);
    toast.success('Deleted');
    const r = await api.get('/calendar/events'); setEvents(r.data);
  };

  const openNew = (dateStr) => {
    setEditEvent(null);
    setForm({ title: '', date: dateStr || todayStr, time: '', color: 'blue', description: '', assignee: '' });
    setDialogOpen(true);
  };

  const selectedEvents = selectedDate ? eventsForDate(selectedDate) : [];

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto" data-testid="calendar-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-[#1e3a50] tracking-tight">Calendar</h1>
          <p className="text-[#6b8a9e] text-sm font-medium mt-0.5">Stage deadlines, project milestones & events</p>
        </div>
        <Button className="bg-[#4a9ac5] hover:bg-[#3d87b3] text-white" onClick={() => openNew(null)} data-testid="add-event-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Event
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Calendar Grid */}
        <div className="lg:col-span-3">
          <Card className="border-0 shadow-[0_1px_4px_rgba(0,0,0,0.05)]">
            <CardHeader className="pb-3 px-5 pt-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Button variant="ghost" size="icon" onClick={prevMonth} className="h-8 w-8" data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></Button>
                  <h2 className="text-lg font-bold text-[#1e3a50] min-w-[180px] text-center">{MONTHS[month]} {year}</h2>
                  <Button variant="ghost" size="icon" onClick={nextMonth} className="h-8 w-8" data-testid="cal-next"><ChevronRight className="w-4 h-4" /></Button>
                </div>
                <Button variant="outline" size="sm" onClick={goToday} className="text-xs font-bold border-[#c8dce8] text-[#4a9ac5]" data-testid="cal-today">Today</Button>
              </div>
            </CardHeader>
            <CardContent className="px-3 pb-4">
              {/* Day headers */}
              <div className="grid grid-cols-7 mb-1">
                {DAYS.map(d => (
                  <div key={d} className="text-center text-[10px] font-bold text-[#6b8a9e] uppercase tracking-wider py-2">{d}</div>
                ))}
              </div>
              {/* Day cells */}
              <div className="grid grid-cols-7 gap-[1px] bg-[#e4f0f6]/50 rounded-lg overflow-hidden">
                {calendarDays.map((cell, i) => {
                  const dateStr = cell.current ? getDateStr(cell.day) : '';
                  const dayEvents = cell.current ? eventsForDate(dateStr) : [];
                  const isToday = dateStr === todayStr;
                  const isSelected = dateStr === selectedDate;
                  return (
                    <div key={i}
                      className={`min-h-[80px] sm:min-h-[100px] p-1.5 cursor-pointer transition-colors ${
                        cell.current ? 'bg-white hover:bg-[#f5f9fb]' : 'bg-[#f7fbfd]'
                      } ${isSelected ? 'ring-2 ring-[#4a9ac5] ring-inset' : ''}`}
                      onClick={() => cell.current && setSelectedDate(dateStr)}
                      onDoubleClick={() => cell.current && openNew(dateStr)}
                      data-testid={`cal-day-${dateStr}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-xs font-bold w-6 h-6 flex items-center justify-center rounded-full ${
                          isToday ? 'bg-[#4a9ac5] text-white' : cell.current ? 'text-[#2a4a60]' : 'text-gray-300'
                        }`}>{cell.day}</span>
                        {dayEvents.length > 0 && (
                          <span className="text-[8px] font-bold text-[#6b8a9e] bg-[#e4f0f6] px-1 rounded">{dayEvents.length}</span>
                        )}
                      </div>
                      <div className="space-y-0.5">
                        {dayEvents.slice(0, 3).map(evt => (
                          <div key={evt.id} className={`text-[9px] font-semibold px-1 py-0.5 rounded border-l-2 truncate ${COLOR_MAP[evt.color] || COLOR_MAP.blue}`}>
                            {evt.title}
                          </div>
                        ))}
                        {dayEvents.length > 3 && (
                          <p className="text-[8px] font-bold text-[#6b8a9e]">+{dayEvents.length - 3} more</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Panel — Selected Day + Upcoming */}
        <div className="space-y-6">
          {/* Selected Day Events */}
          <Card className="border-0 shadow-[0_1px_4px_rgba(0,0,0,0.05)]">
            <CardHeader className="pb-2 px-4 pt-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-bold text-[#1e3a50]">
                  {selectedDate ? new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' }) : 'Select a date'}
                </CardTitle>
                {selectedDate && (
                  <Button size="sm" variant="ghost" className="h-7 text-[10px] text-[#4a9ac5] font-bold" onClick={() => openNew(selectedDate)}>
                    <Plus className="w-3 h-3 mr-0.5" /> Add
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              {selectedEvents.length === 0 ? (
                <p className="text-xs font-medium text-gray-400 text-center py-6">No events</p>
              ) : (
                <div className="space-y-2">
                  {selectedEvents.map(evt => (
                    <div key={evt.id} className={`p-2.5 rounded-lg border-l-[3px] ${COLOR_MAP[evt.color] || COLOR_MAP.blue}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-bold truncate">{evt.title}</p>
                          {evt.assignee && <p className="text-[10px] font-medium opacity-70">{evt.assignee}</p>}
                          {evt.time && <p className="text-[10px] font-medium opacity-70">{evt.time}</p>}
                          {evt.description && <p className="text-[10px] opacity-60 mt-0.5 line-clamp-2">{evt.description}</p>}
                        </div>
                        {evt.editable && (
                          <div className="flex gap-0.5 ml-1">
                            <button onClick={() => { setEditEvent(evt); setForm({ title: evt.title, date: evt.date, time: evt.time || '', color: evt.color, description: evt.description || '', assignee: evt.assignee || '' }); setDialogOpen(true); }}>
                              <Pencil className="w-3 h-3 opacity-40 hover:opacity-100" />
                            </button>
                            <button onClick={() => handleDelete(evt.id)}>
                              <Trash2 className="w-3 h-3 opacity-40 hover:opacity-100 text-red-400" />
                            </button>
                          </div>
                        )}
                      </div>
                      <Badge variant="outline" className="text-[8px] mt-1 capitalize opacity-60">{evt.type}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Upcoming Events */}
          <Card className="border-0 shadow-[0_1px_4px_rgba(0,0,0,0.05)]">
            <CardHeader className="pb-2 px-4 pt-4">
              <CardTitle className="text-sm font-bold text-[#1e3a50]">Upcoming</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <div className="space-y-1.5">
                {events
                  .filter(e => e.date >= todayStr)
                  .sort((a, b) => a.date.localeCompare(b.date))
                  .slice(0, 8)
                  .map(evt => (
                    <div key={evt.id} className="flex items-center gap-2 py-1.5 cursor-pointer hover:bg-[#f5f9fb] rounded px-1 -mx-1"
                      onClick={() => { setSelectedDate(evt.date); setCurrentDate(new Date(evt.date + 'T12:00:00')); }}>
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                        evt.color === 'green' || evt.color === 'emerald' ? 'bg-emerald-500' :
                        evt.color === 'orange' || evt.color === 'amber' ? 'bg-amber-500' :
                        evt.color === 'red' ? 'bg-red-500' : 'bg-[#4a9ac5]'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-[11px] font-bold text-[#2a4a60] truncate">{evt.title}</p>
                      </div>
                      <span className="text-[9px] font-bold text-[#6b8a9e] flex-shrink-0">
                        {new Date(evt.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                    </div>
                  ))}
                {events.filter(e => e.date >= todayStr).length === 0 && (
                  <p className="text-xs font-medium text-gray-400 text-center py-4">No upcoming events</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Add/Edit Event Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="event-dialog">
          <DialogHeader><DialogTitle>{editEvent ? 'Edit Event' : 'Add Event'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs font-bold">Title *</Label><Input value={form.title} onChange={e => setForm(p => ({...p, title: e.target.value}))} placeholder="Meeting, inspection, deadline..." data-testid="event-title" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-bold">Date</Label><Input type="date" value={form.date} onChange={e => setForm(p => ({...p, date: e.target.value}))} data-testid="event-date" /></div>
              <div><Label className="text-xs font-bold">Time (optional)</Label><Input type="time" value={form.time} onChange={e => setForm(p => ({...p, time: e.target.value}))} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-bold">Assignee</Label><Input value={form.assignee} onChange={e => setForm(p => ({...p, assignee: e.target.value}))} placeholder="Name..." /></div>
              <div>
                <Label className="text-xs font-bold">Color</Label>
                <div className="flex gap-1.5 mt-1.5">
                  {COLORS.map(c => (
                    <button key={c} onClick={() => setForm(p => ({...p, color: c}))}
                      className={`w-6 h-6 rounded-full border-2 transition-all ${
                        c === 'blue' ? 'bg-[#4a9ac5]' : c === 'green' ? 'bg-emerald-500' :
                        c === 'orange' ? 'bg-orange-500' : c === 'red' ? 'bg-red-500' :
                        c === 'violet' ? 'bg-violet-500' : 'bg-amber-500'
                      } ${form.color === c ? 'border-[#1e3a50] scale-110' : 'border-transparent'}`}
                      data-testid={`color-${c}`} />
                  ))}
                </div>
              </div>
            </div>
            <div><Label className="text-xs font-bold">Description</Label><Textarea value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))} rows={2} placeholder="Optional notes..." /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button className="bg-[#4a9ac5] hover:bg-[#3d87b3] text-white" onClick={handleSave} disabled={!form.title.trim() || !form.date} data-testid="event-save-btn">{editEvent ? 'Update' : 'Add Event'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
